import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, Github, Calendar, Award, Target, Zap, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollingScreenshotPreview } from '@/components/ui/scrolling-screenshot-preview';
import { useProjectBySlug } from '@/hooks/use-projects';
import { ProjectHero } from '@/components/project/ProjectHero';
import { ProjectKPIs } from '@/components/project/ProjectKPIs';
import { PremiumProjectGallery } from '@/components/project/PremiumProjectGallery';
import { BeforeAfterSlider } from '@/components/ui/before-after-slider';
import ProceduralGroundBackground from '@/components/ui/procedural-ground-background';
import { motion } from 'framer-motion';

const ProjetoDetalhe = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: project, isLoading, error } = useProjectBySlug(slug || '');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full mx-auto mb-4"
          />
          <p className="text-lg text-gray-400">Carregando projeto...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center bg-gradient-to-br from-slate-950 to-slate-900">
        <h2 className="text-3xl font-bold text-white mb-4">Projeto não encontrado</h2>
        <p className="text-gray-400 mb-8 text-lg">
          O projeto que você procura não existe ou foi removido.
        </p>
        <Button asChild variant="outline" className="border-white/10 text-white hover:bg-white/5">
          <Link to="/projetos" className="flex items-center gap-2">
            <ArrowLeft className="h-5 w-5" />
            Voltar para Projetos
          </Link>
        </Button>
      </div>
    );
  }

  const galleryImages = project.gallery.map((img: any) => ({
    src: img.src,
    alt: img.alt,
    caption: img.caption,
    size: img.size || 'medium',
  }));

  const kpis = project.kpis.map((kpi: any) => ({
    label: kpi.label,
    value: kpi.value
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-x-hidden">
      <ProceduralGroundBackground className="fixed inset-0 w-full h-full z-0 opacity-20 pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <ProjectHero
          title={project.title}
          summary={project.summary}
          coverImage={project.coverImage}
          client={project.client}
          year={project.year}
          disciplines={project.disciplines}
        />

        {kpis.length > 0 && (
          <section className="py-24 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 via-blue-500/5 to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
            <div className="w-[90%] mx-auto px-0 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
                  <Award className="w-10 h-10 text-blue-400" />
                  <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
                    Resultados & Impactos
                  </span>
                </h2>
                <p className="text-xl text-gray-400">
                  Métricas de sucesso alcançadas
                </p>
              </motion.div>
              <ProjectKPIs kpis={kpis} />
            </div>
          </section>
        )}

        <PremiumProjectGallery images={galleryImages} />

 {(project as any).auto_scroll && project.coverImage && (
          <section className="py-24 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/5 via-transparent to-transparent" />
            <div className="container mx-auto px-4 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
                  <Monitor className="w-10 h-10 text-indigo-400" />
                  <span className="bg-gradient-to-r from-indigo-400 via-blue-300 to-cyan-400 bg-clip-text text-transparent">
                    Visualização Completa
                  </span>
                </h2>
                <p className="text-xl text-gray-400">
                  Role para ver todo o projeto
                </p>
              </motion.div>
              <div className="max-w-6xl mx-auto">
                <ScrollingScreenshotPreview
                  src={project.coverImage}
                  alt={`Screenshot completo de ${project.title}`}
                  enableAutoScroll={true}
                  height="600px"
                />
              </div>
            </div>
          </section>
        )}

        {project.beforeAfter && (
          <section className="py-24 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/5 via-transparent to-transparent" />
            <div className="container mx-auto px-4 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
                  <Zap className="w-10 h-10 text-orange-400" />
                  Transformação
                </h2>
                <p className="text-xl text-gray-400">
                  Veja a diferença antes e depois
                </p>
              </motion.div>
              <div className="max-w-5xl mx-auto">
                <BeforeAfterSlider
                  beforeImage={project.beforeAfter.before}
                  afterImage={project.beforeAfter.after}
                  beforeLabel="Antes"
                  afterLabel="Depois"
                />
                <p className="text-center text-gray-500 mt-6 text-sm">
                  Arraste o slider para comparar o antes e depois.
                </p>
              </div>
            </div>
          </section>
        )}

        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-transparent" />
          <div className="w-[90%] mx-auto px-0 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              
              {project.tools && project.tools.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 p-8 rounded-3xl border border-blue-500/20 backdrop-blur-sm"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600/20 to-indigo-600/20 flex items-center justify-center">
                      <Target className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                      Stack & Ferramentas
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {project.tools.map((tool: string, index: number) => (
                      <motion.span
                        key={tool}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="px-4 py-2 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-sm font-medium hover:bg-blue-500/20 transition-colors"
                      >
                        {tool}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              )}

              {project.press && project.press.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 p-8 rounded-3xl border border-indigo-500/20 backdrop-blur-sm"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600/20 to-purple-600/20 flex items-center justify-center">
                      <ExternalLink className="w-6 h-6 text-indigo-400" />
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                      Na Mídia
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    {project.press.map((item: any, index: number) => (
                      <motion.li
                        key={item.url}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl transition-all duration-300 group"
                        >
                          <span className="text-gray-300 font-medium group-hover:text-white transition-colors">
                            {item.outlet}
                          </span>
                          <ExternalLink className="h-5 w-5 text-gray-500 group-hover:text-indigo-400 transition-colors" />
                        </a>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              )}

            </div>
          </div>
        </section>

        <section className="py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-blue-600/5 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center space-y-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-sm font-medium">
                <Award className="w-4 h-4" />
                <span>Pronto para começar?</span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                Gostou do{" "}
                <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  resultado?
                </span>
              </h2>

              <p className="text-xl text-gray-400 leading-relaxed">
                Vamos criar algo extraordinário para o seu projeto também.
                Transforme sua visão em realidade com designs e experiências digitais impactantes.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg h-auto shadow-lg shadow-blue-500/20">
                  <Link to="/contato">
                    Iniciar Projeto
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-8 py-6 text-lg h-auto border-white/10 text-white hover:bg-white/5">
                  <Link to="/projetos">
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Ver Outros Projetos
                  </Link>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap justify-center gap-8 pt-8"
              >
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm">{project.year}</span>
                </div>
                {project.client && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Target className="w-5 h-5" />
                    <span className="text-sm">{project.client}</span>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default ProjetoDetalhe;
